import Box from '@mui/material/Box';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { useController, useFormContext } from 'react-hook-form';
import { StyledTargetingParameterLabelTypography } from '../../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import type { RequiredTargetingSectionKeysType } from '../context/types.ts';
import { RequiredTargetingElement } from '../RequiredTargetingElement/RequiredTargetingElement.tsx';
import type { TargetingPresetElementProps } from '../TargetingPresetElement/TargetingPresetElement.tsx';

export type TargetingPresetSectionProps = {
  label: string;
  elements: TargetingPresetElementProps[];
  isLoading?: boolean;
  sectionKey: RequiredTargetingSectionKeysType;
  noMarginTop?: boolean;
  name: 'device_type' | 'distribution_channel' | 'ad_placement';
  readOnly?: boolean;
};

export const TargetingPresetSection = ({ readOnly, ...props }: TargetingPresetSectionProps) => {
  const formMethods = useFormContext();

  return !readOnly && formMethods ? (
    <TargetingPresetFormSection {...props} />
  ) : (
    <TargetingPresetPreviewSection {...props} />
  );
};

const TargetingPresetFormSection = (props: TargetingPresetSectionProps) => {
  useController({ name: `targeting.rendering_context.${props.name}` });

  return <TargetingPresetPreviewSection {...props} />;
};

const TargetingPresetPreviewSection = ({
  label,
  elements,
  isLoading = false,
  sectionKey,
  noMarginTop = false,
}: TargetingPresetSectionProps) => {
  return (
    <Box data-test="deal-preset-section" sx={theme => ({ gap: theme.spacing(3) })}>
      <StyledTargetingParameterLabelTypography
        variant="h3"
        sx={{ mb: 1, mt: noMarginTop ? 0 : 2 }}
        data-test={`section-${label.toLowerCase()}`}
      >
        {label}
      </StyledTargetingParameterLabelTypography>

      <Box sx={theme => ({ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) })}>
        {isLoading ? (
          <Loader noPadding data-test="inventory-targeting-loader" />
        ) : (
          elements.map((element, index) => (
            <RequiredTargetingElement key={index} sectionKey={sectionKey} elementIndex={index} {...element} />
          ))
        )}
      </Box>
    </Box>
  );
};
